// Section Types and Variant Constants
// This file contains all the type identifiers and variant constants used across the application
//
// ─────────────────────────────────────────────────────────────────────────────
// MANDATORY when you add a SECTION TYPE or a VARIANT
// ─────────────────────────────────────────────────────────────────────────────
// The ids below are only half the registration. Several admin screens — most
// notably **Widget Management** (`/admin/widget-management`), which lists every
// section and variant in use across all pages — render their labels by looking
// the id up in the registries listed here. Skip a step and the id still works,
// but it surfaces to editors as a bare number ("Section 3006", "Variant 1051")
// with no name, description or icon, in a screen whose whole job is to let
// someone identify a widget before deleting it from live pages.
//
// Adding a NEW SECTION TYPE — do ALL of these:
//   1. `SECTION_TYPES` below, AND `SECTION_TYPES` in server `constants.js`.
//      The two must hold the identical string value.
//   2. `SECTION_METADATA` in ./sectionMetadata.ts — name, description, icon.
//      Widget Management groups and labels the left panel from this.
//   3. A variants array for it in ./sectionVariants/<group>.ts, spread into
//      SECTION_VARIANTS by ./sectionVariants/index.ts. `getVariantDetails()`
//      reads this; the page builder and Widget Management both call it.
//
// Adding a NEW VARIANT of an existing section — do ALL of these:
//   1. The section's `*_VARIANTS` map below, if it has one.
//   2. An entry in that section's array in ./sectionVariants/<group>.ts, with a
//      human-readable `name` — that string is what Widget Management shows.
//   3. A server migration widening the widget table's `variant` ENUM (see
//      server/migrations/*-variant-*.js for the pattern) — without it, inserts
//      of the new variant are coerced or rejected by MySQL.
//
// Rule of thumb: if an editor could ever see this id in the admin UI, it needs a
// name. Treat steps 2 and 3 as part of "the widget is done", not as follow-up.
// ─────────────────────────────────────────────────────────────────────────────

export const UNIVERSITY_ID = 1;

/** String values match server `page.page_type` ENUM and public `constants.js` PAGE_TYPES. */
export const PAGE_TYPES = {
	HOME: "home",
	PROGRAM: "program",
	SPECIALISATION: "specialisation",
	AUXILIARY: "auxiliary",
	LANDING_HOME: "landing_home",
	LANDING_PROGRAM: "landing_program",
	OTHERS: "others",
	BLOG: "blog",
};

export const SECTION_TYPES = {
	BANNER: "1",
	HEADER: "2",
	MENU_ITEM: "3",
	ABOUT: "4",
	KEY_NUMBERS: "5",
	ACCREDITATION: "6",
	ACADEMIC_DELIVERABLES: "7",
	SCHOLAR: "8",
	RECRUITERS: "9",
	VICE_CHANCELLOR: "10",
	FACULTY: "11",
	TESTIMONIALS: "12",
	MASTERCLASS: "13",
	MESSAGE: "14",
	PLACEMENTS: "15",
	MOBILE_APP: "16",
	FAQ: "17",
	PROGRAM_STRUCTURE: "29",
	ELECTIVE: "30",
	PROGRAM_FEE: "31",
	RICH_TEXT_EDITOR: "32",
	FOOTER: "18",
	SUBSCRIBE: "19",
	PROGRAM_CARDS: "20",
	SPECIALISATION_AVAILABLE: "21",
	CAREER_PROSPECTS: "22",
	SUCCESS_STORIES: "23",
	WHAT_MAKES_COURSE_DIFFERENT: "24",
	ELIGIBILITY: "25",
	THANK_YOU: "26",
	REFERRAL_FORM: "27",
	INFLUENCER_FORM: "28",
	MOBILE_LEAD_FORM: "33",
	COUNSELING: "34",
	PROGRAM_PROSPECTS: "35",
	COURSE_SPECIALISATIONS: "36",
	ACCORDION: "37",
	LIVE_LECTURE: "38",
	PROGRAM_BENEFITS: "39",
	COURSE_OUTCOME: "40",
	CERTIFICATION: "41",
	APPLICATION_PROCESS: "42",
	SKILLS: "43",
	WHO_IS_THIS_PROGRAM_FOR: "44",
	BANNER_VIDEO: "45",
	COMPARISON: "46",
	LEADERS: "47",
	DIGITAL_LEARNING: "48",
	VISIONARIES: "49",
	PARA_TEXT: "50",
	WALK_THROUGH: "62",
	LIFE_MILESTONE: "63",
	ENROLLMENTS: "64",
	/** Blog article + embedded lead form (separate from RICH_TEXT_EDITOR). */
	BLOG_WITH_LEAD: "51",
	/** Latest blog cards + carousel (Atlas). */
	LATEST_BLOGS: "52",
	/** Phone pill + call counselor CTA (Atlas). */
	COUNSELOR_CALL_CTA: "53",
	/** Manual breadcrumb strip (blog article column on blog pages). */
	BREADCRUMB_BANNER: "54",
	/** Blog list with categories, cards, load more (Atlas). */
	BLOG_LIST: "55",
	/** Inline image + caption in the blog article column. */
	BLOG_INLINE_IMAGE: "56",
	/** Popular blog list rail (Atlas Figma ~7749:17054 / ~7649:4418). */
	POPULAR_BLOGS: "57",
	/** Standalone widget (separate from Visionaries); reuses Visionaries CMS/API. */
	LEARN_FROM_LEADERS: "58",
	CASE_STUDY: "59",
	/** Brand pills grid ("Solve real business challenges for leading brands"). */
	BRAND: "60",
	/** Institutes text-chip row ("Graduates build careers across leading financial institutions"). */
	INSTITUTES: "61",
	/** Configurable grievance/contact form that emails submissions via Resend. */
	GRIEVANCE_FORM: "3000",
	/** Configurable heading/title text block. Custom widget id range (3000+). */
	HEADING: "3001",
	/** Author profile: author card + about + their blogs. Custom widget id range (3000+). */
	AUTHOR_PROFILE: "3002",
	/** Author card: compact author bio card. Blog pages with a linked author only. */
	AUTHOR_CARD: "3003",
	/** Author byline: "By <author>" + updated/read-time/views + share. Blog pages with a linked author only. */
	AUTHOR_BYLINE: "3004",
	/** Programme-specialisation card carousel (image cards / icon cards). Custom widget id range (3000+). */
	SPECIALISATION_CAROUSEL: "3005",
	SKILL_CARDS: "3006",

	/**
	 * Reserved, non-widget section: a placement of a reusable Widget Block.
	 * Has no variants / settings form — the whole collection is one unit on the page.
	 */
	WIDGET_BLOCK: "9000",
};

export const BANNER_VARIANTS = {
	BANNER_1: "1",
	BANNER_2: "2",
	BANNER_3: "3",
	BANNER_4: "4",
	BANNER_5: "5",
	BANNER_6: "6",
	BANNER_8: "8",
	BANNER_9: "9",
	BANNER_15: "15",
	BANNER_1010: "1010",
	/** Program-page Hero Banner (value-prop column + inline LeadDrop9 form). */
	BANNER_10: "10",
	/** Middle Banner (ASU-010): full-bleed bg image + left image + content + dual CTAs. */
	BANNER_11: "11",
	/** Copy of Banner 1 with a shorter desktop height. Custom widget id range (3000+). */
	BANNER_3000: "3000",
	/** KUK auxiliary page hero — background image + white scrim + centred title and icon links. */
	BANNER_3001: "3001",
	/** KUK marketing banner — full-bleed CTA band (heading + line + button) over a background photo. */
	BANNER_5004: "5004",
	/** BDU Hero — full-bleed slide carousel + left gradient overlay card + accreditation bar. */
	BANNER_1025: "1025",
	/** BDU Programme Hero — Banner 1025 layout with an h2 sub-heading and Apply + Brochure CTAs. */
	BANNER_1035: "1035",
  /** BDU auxiliary page hero — static image + left gradient card + accreditation bar. Pairs with Rich Text Editor 6001. */
  BANNER_6000: "6000",
	/** KUK home hero — slide carousel behind a white card (Welcome to / name / Online / CTA). */
	BANNER_1051: "1051",
	/** KUK home "Want to set yourself apart" band — photo + scrim + icon + heading + outline CTA. */
	BANNER_1058: "1058",
	/** KUK home referral band — artwork background, highlighted heading, gradient pill CTA. */
	BANNER_1059: "1059",
	/** KUK programme hero — three-slide carousel, each slide its own layout, plus an accreditation rail. */
	BANNER_1061: "1061",
	/** KUK referral band + USP strip — Banner 1059's band with a three-column USP row under it. */
	BANNER_1062: "1062",
	/** KUK launchpad access band — artwork background, gift icon, heading and an outline CTA. */
	BANNER_1068: "1068",
};

export const HEADER_VARIANTS = {
	HEADER_1: "1",
	HEADER_2: "2",
	HEADER_3: "3",
	HEADER_4: "4",
	HEADER_5: "5",
	HEADER_6: "6",
	HEADER_7: "7",
	/** BDU contact strip — right-aligned phone + WhatsApp pills on a solid bar, desktop only. */
	HEADER_1046: "1046",
	/** Promotional pill ("Admissions closing soon"), optionally pinned over the page. */
	PROMO_PILL: "5000",
	/** BDU site header — logo + phone line + WhatsApp button + Apply CTA. */
	HEADER_5001: "5001",
	/** KUK landing-page site header — logo, WhatsApp link, phone line, Apply CTA. */
	HEADER_5015: "5015",
	/** KUK promotional marquee — one line of promo copy scrolling on a tinted bar. */
	HEADER_5016: "5016",
};

export const MENU_ITEM_VARIANTS = {
	MENU_ITEM_1: "1",
	MENU_ITEM_2: "2",
	MENU_ITEM_3: "3",
	MENU_ITEM_4: "4",
	MENU_ITEM_5: "5",
	/** Atlas "Nav menu": black bar, logo + links + phone + WhatsApp + mobile hamburger. */
	MENU_ITEM_2000: "2000",
	/** KUK site nav: white bar, logo + links with programme mega-menu + Apply CTA. */
	MENU_ITEM_1060: "1060",
	/** BDU nav: white bar, logo + links with 3-level dropdowns + gradient Apply CTA. */
	MENU_ITEM_1047: "1047",
};

export const ABOUT_VARIANTS = {
	ABOUT_1: "1",
	ABOUT_2: "2",
	ABOUT_3: "3",
	ABOUT_4: "4",
	ABOUT_5: "5",
	/** BDU About Us — square image + gradient rule + Know More link. */
	ABOUT_1028: "1028",
	/** BDU About the programme — heading + rule + justified copy + keypoint chips + inline lead form. */
	ABOUT_1037: "1037",
	/** KUK About the programme — heading, intro copy and key-content cards (scroll rail from 768px, accordion below). */
	ABOUT_5005: "5005",
	/** KUK home About Us — YouTube facade beside justified copy and a Know More CTA. */
	ABOUT_1054: "1054",
	/** KUK home Vice Chancellor's message — portrait card beside vision / mission cards. */
	ABOUT_1057: "1057",
};

export const KEY_NUMBERS_VARIANTS = {
	KEY_NUMBERS_1: "1",
	KEY_NUMBERS_2: "2",
	/** BDU Stats — circled icon + count + label on a solid bar. */
	KEY_NUMBERS_1026: "1026",
	/** BDU Programme key features — circled icon + single line of copy on a solid bar. */
	KEY_NUMBERS_1036: "1036",
	/** BDU Programme key content — big number + label on a solid bar, hairline separators. */
	KEY_NUMBERS_1039: "1039",
	/** KUK programme key features — icon over one line of copy on a tinted strip. */
	KEY_NUMBERS_5006: "5006",
	/** KUK learning highlights — big figure over its label on a gradient band. */
	KEY_NUMBERS_5007: "5007",
	/** KUK home Stats — icon over a formatted count and its label on a gradient bar. */
	KEY_NUMBERS_1055: "1055",
};

export const DIGITAL_LEARNING_VARIANTS = {
	DIGITAL_LEARNING_1: "1",
};

export const VISIONARIES_VARIANTS = {
	VISIONARIES_1: "1",
};

export const LEARN_FROM_LEADERS_VARIANTS = {
	LEARN_FROM_LEADERS_1: "1",
	LEARN_FROM_LEADERS_1003: "1003",
};

export const PARA_TEXT_VARIANTS = {
	PARA_TEXT_1: "1",
};

export const ACCREDITATION_VARIANTS = {
	ACCREDITATION_1: "1",
	ACCREDITATION_2: "2",
	ACCREDITATION_3: "3",
	ACCREDITATION_4: "4",
	ACCREDITATION_5: "5",
	ACCREDITATION_1000: "1000",
	/** KUK home accreditation strip — badge over a label, hairline rules between. */
	ACCREDITATION_1052: "1052",
};

export const ACADEMIC_DELIVERABLES_VARIANTS = {
	ACADEMIC_DELIVERABLES_1: "1",
	ACADEMIC_DELIVERABLES_2: "2",
	ACADEMIC_DELIVERABLES_1001: "1001",
	ACADEMIC_DELIVERABLES_1002: "1002",
	ACADEMIC_DELIVERABLES_1006: "1006",
	/** BDU Hands-On Learning — tinted bg image + numbered icon rows. */
	ACADEMIC_DELIVERABLES_1031: "1031",
};

export const SCHOLAR_VARIANTS = {
	SCHOLAR_1: "1",
};

export const MESSAGE_VARIANTS = {
	MESSAGE_1: "1",
};

export const FAQ_VARIANTS = {
	FAQ_1: "1",
	FAQ_2: "2",
	FAQ_3: "3",
	FAQ_4: "4",
	/** BDU FAQs — numbered accordion with tinted open state + Contact Us CTA. */
	FAQ_1033: "1033",
	/** KUK FAQs — single-open accordion under a ruled heading, with a Contact Us CTA. */
	FAQ_5013: "5013",
};

export const PROGRAM_STRUCTURE_VARIANTS = {
	PROGRAM_STRUCTURE_1: "1",
	PROGRAM_STRUCTURE_2: "2",
	PROGRAM_STRUCTURE_3: "3",
	PROGRAM_STRUCTURE_4: "4",
	PROGRAM_STRUCTURE_5: "5",
	/** BDU Programme Syllabus — semester tabs + subject list on a tinted column, art on the right. */
	PROGRAM_STRUCTURE_1042: "1042",
	/** KUK Programme Structure — brochure CTA beside a semester accordion of subject tables. */
	PROGRAM_STRUCTURE_1064: "1064",
};

export const ELECTIVE_VARIANTS = {
	ELECTIVE_1: "1",
	/** KUK Electives — elective tabs down the left, a subject/semester table on the right. */
	ELECTIVE_1065: "1065",
};

export const PROGRAM_FEE_VARIANTS = {
	PROGRAM_FEE_1: "1",
	PROGRAM_FEE_2: "2",
	PROGRAM_FEE_3: "3",
	/** BDU Fee Structure — student-type pills, fee-plan list, line-item table (Program Fee 1 data). */
	PROGRAM_FEE_1043: "1043",
	/** KUK Fee Structure — student-type tabs over plan cards (Program Fee 2 JSON data). */
	PROGRAM_FEE_1066: "1066",
};

export const RICH_TEXT_EDITOR_VARIANTS = {
	RICH_TEXT_EDITOR_1: "1",
  /** BDU auxiliary page body — article column + 328px lead form rail. Pairs with Banner 6000. */
  RICH_TEXT_EDITOR_6001: "6001",
};

export const BLOG_WITH_LEAD_VARIANTS = {
	BLOG_WITH_LEAD_1: "1",
};

export const LATEST_BLOGS_VARIANTS = {
	LATEST_BLOGS_1: "1",
	LATEST_BLOGS_2: "2",
};

export const POPULAR_BLOGS_VARIANTS = {
	POPULAR_BLOGS_1: "1",
};

export const COUNSELOR_CALL_CTA_VARIANTS = {
	COUNSELOR_CALL_CTA_1: "1",
};

export const BREADCRUMB_BANNER_VARIANTS = {
	BREADCRUMB_BANNER_1: "1",
};

export const BLOG_LIST_VARIANTS = {
	BLOG_LIST_1: "1",
};

export const BLOG_INLINE_IMAGE_VARIANTS = {
	BLOG_INLINE_IMAGE_1: "1",
};

export const RECRUITERS_VARIANTS = {
	RECRUITERS_1: "1",
	/** KUK Where our graduates work — heading, sub-heading and a row of employer logos. */
	RECRUITERS_5010: "5010",
};

export const VICE_CHANCELLOR_VARIANTS = {
	VICE_CHANCELLOR_1: "1",
};

export const ELIGIBILITY_VARIANTS = {
	ELIGIBILITY_1: "1",
	ELIGIBILITY_2: "2",
	/** BDU Eligibility Criteria — heading + rule + single tinted panel with a leading icon. */
	ELIGIBILITY_1041: "1041",
	/** KUK Eligibility Criteria — heading over one tinted panel with a leading icon. */
	ELIGIBILITY_5012: "5012",
};

export const SPECIALISATION_AVAILABLE_VARIANTS = {
	SPECIALISATION_AVAILABLE_1: "1",
	/** KUK programme-specialisation cards. Custom widget id range (5000+). */
	SPECIALISATION_AVAILABLE_5017: "5017",
};

export const CAREER_PROSPECTS_VARIANTS = {
	CAREER_PROSPECTS_1: "1",
	CAREER_PROSPECTS_2: "2",
	CAREER_PROSPECTS_3: "3",
	CAREER_PROSPECTS_4: "4",
};

export const SUCCESS_STORIES_VARIANTS = {
	SUCCESS_STORIES_1: "1",
	/** Student Showcase (ASU-009): heading + subtitle + paged image-card carousel. */
	SUCCESS_STORIES_2: "2",
};

export const WHAT_MAKES_COURSE_DIFFERENT_VARIANTS = {
	WHAT_MAKES_COURSE_DIFFERENT_1: "1",
	WHAT_MAKES_COURSE_DIFFERENT_2: "2",
};

export const FACULTY_VARIANTS = {
	FACULTY_1: "1",
	FACULTY_2: "2",
	FACULTY_3: "3",
	FACULTY_4: "4",
	FACULTY_5: "5",
	FACULTY_6: "6",
};

export const TESTIMONIALS_VARIANTS = {
	TESTIMONIALS_1: "1",
	TESTIMONIALS_2: "2",
	/** KUK home student testimonials — scroll rail of portrait quote cards with dim-when-offscreen. */
	TESTIMONIALS_1056: "1056",
};

export const MASTERCLASS_VARIANTS = {
	MASTERCLASS_1: "1",
};

export const PLACEMENTS_VARIANTS = {
	PLACEMENTS_1: "1",
	PLACEMENTS_2: "2",
	PLACEMENTS_3: "3",
	PLACEMENTS_4: "4",
};

export const MOBILE_APP_VARIANTS = {
	MOBILE_APP_1: "1",
};

export const FOOTER_VARIANTS = {
	FOOTER_1: "1",
	FOOTER_2: "2",
	FOOTER_3: "3",
	/** BDU Footer — bg image, link columns, disclaimer + copyright. */
	FOOTER_1034: "1034",
	/** KUK Footer — solid band with optional link columns and a copyright bar. */
	FOOTER_5014: "5014",
};

export const SUBSCRIBE_VARIANTS = {
	SUBSCRIBE_1: "1",
};

export const PROGRAM_CARDS_VARIANTS = {
	PROGRAM_CARDS_1: "1",
	PROGRAM_CARDS_2: "2",
	PROGRAM_CARDS_3: "3",
	PROGRAM_CARDS_4: "4",
	PROGRAM_CARDS_BLOG_1: "5",
	/** BDU Featured Programmes — Bachelors/Masters tabs + image cards. */
	PROGRAM_CARDS_1027: "1027",
	/** BDU Specialisations — heading + rule + wrapping grid of tinted programme cards. */
	PROGRAM_CARDS_1038: "1038",
	/** BDU Other Specialisations — left-aligned heading + horizontal card rail. */
	PROGRAM_CARDS_1045: "1045",
  /** BDU auxiliary page "Explore Our Programs" — tinted band + card rail. Closes the Banner 6000 / RTE 6001 stack. */
  PROGRAM_CARDS_6002: "6002",
	/** KUK home Featured Programmes — level tabs, programme rail, detail card, specialisation grid. */
	PROGRAM_CARDS_1053: "1053",
	/** KUK Explore other Specialisations — eyebrow heading beside a row of specialisation cards. */
	PROGRAM_CARDS_1067: "1067",
	/** KUK Explore other courses — artwork band, four-up card grid on desktop, rail below. */
	PROGRAM_CARDS_1070: "1070",
};

export const THANK_YOU_VARIANTS = {
	THANK_YOU_1: "1",
	THANK_YOU_1009: "1009",
};

export const REFERRAL_FORM_VARIANTS = {
	REFERRAL_FORM_1: "1",
	REFERRAL_FORM_1008: "1008",
	/** BDU-styled referral (refurl) form */
	REFERRAL_FORM_1048: "1048",
};

export const INFLUENCER_FORM_VARIANTS = {
	INFLUENCER_FORM_1: "1",
	INFLUENCER_FORM_1007: "1007",
	/** BDU-styled influencer form */
	INFLUENCER_FORM_1049: "1049",
};

export const GRIEVANCE_FORM_VARIANTS = {
	GRIEVANCE_FORM_1: "3000",
};

export const MOBILE_LEAD_FORM_VARIANTS = {
	MOBILE_LEAD_FORM_1: "1",
	MOBILE_LEAD_FORM_2: "2",
	MOBILE_LEAD_FORM_6: "6",
};

export const COUNSELING_VARIANTS = {
	COUNSELING_1: "1",
};

export const PROGRAM_PROSPECTS_VARIANTS = {
	PROGRAM_PROSPECTS_1: "1",
};

export const COURSE_SPECIALISATIONS_VARIANTS = {
	COURSE_SPECIALISATIONS_1: "1",
};

export const ACCORDION_VARIANTS = {
	ACCORDION_1: "1",
};

export const LIVE_LECTURE_VARIANTS = {
	LIVE_LECTURE_1: "1",
	LIVE_LECTURE_1011: "1011",
	LIVE_LECTURE_1050: "1050",
};

export const PROGRAM_BENEFITS_VARIANTS = {
	PROGRAM_BENEFITS_1: "1",
	PROGRAM_BENEFITS_2: "2",
	PROGRAM_BENEFITS_3: "3",
	PROGRAM_BENEFITS_4: "4",
	PROGRAM_BENEFITS_5: "5",
	PROGRAM_BENEFITS_6: "6",
	PROGRAM_BENEFITS_7: "7",
	/**
	 * Degree grid — Figma "Degree that moves with you": a 3x2 icon-card grid.
	 *
	 * Lived at "2" until the unified-tenancy merge repointed "2" at the Program
	 * Benefits 2 CAROUSEL, which renders from a different column set entirely
	 * (`description` / `left_navigation_icon` / `enable_stack_carousel`, where the
	 * grid fills `subtitle` / `section_background_image`). Moving the grid to an
	 * uncontested id ends that collision for good instead of re-fighting it on
	 * every merge. Its fields still live in `programBenefits_12.ts` and its rows in
	 * the `program_benefits_12` slice — only the id changed.
	 */
	PROGRAM_BENEFITS_3000: "3000",
	PROGRAM_BENEFITS_1005: "1005",
	/** BDU Key Benefits — tinted bg image + icon/title/description cards. */
	PROGRAM_BENEFITS_1029: "1029",
	/** BDU Programme Roadmap / Key Programme Features — bg art + icon/title/description columns. */
	PROGRAM_BENEFITS_1040: "1040",
	/** BDU Learn More — heading + rule + rich-text cards with a Read more clamp. */
	PROGRAM_BENEFITS_1044: "1044",
	/** KUK Why-KUK-Online cards — washed-out photo behind an icon, title and copy. */
	PROGRAM_BENEFITS_5008: "5008",
	/** KUK Career Roadmap — icon/role/description cards plus a counsellor CTA. */
	PROGRAM_BENEFITS_5009: "5009",
	/** KUK "Why Choose Us?" — icon/title/description cards as a rail above 768px, a two-column grid below. */
	PROGRAM_BENEFITS_1063: "1063",
	/** KUK Learn More — tinted band of rich-text cards with a View more clamp. */
	PROGRAM_BENEFITS_1069: "1069",
};

export const CASE_STUDY_VARIANTS = {
	CASE_STUDY_1: "1",
};

export const BRAND_VARIANTS = {
	BRAND_1: "1",
};

export const INSTITUTES_VARIANTS = {
	INSTITUTES_1: "1",
};

export const HEADING_VARIANTS = {
	HEADING_1: "3000",
};

export const AUTHOR_PROFILE_VARIANTS = {
	AUTHOR_PROFILE_1: "3000",
};

export const AUTHOR_CARD_VARIANTS = {
	AUTHOR_CARD_1: "3000",
};

export const AUTHOR_BYLINE_VARIANTS = {
	AUTHOR_BYLINE_1: "3000",
};

export const SPECIALISATION_CAROUSEL_VARIANTS = {
	SPECIALISATION_CAROUSEL_1: "3000",
};

export const COURSE_OUTCOME_VARIANTS = {
	COURSE_OUTCOME_1: "1",
	COURSE_OUTCOME_2: "2",
};

export const CERTIFICATION_VARIANTS = {
	CERTIFICATION_1: "1",
};

export const APPLICATION_PROCESS_VARIANTS = {
	APPLICATION_PROCESS_1: "1",
	APPLICATION_PROCESS_2: "2",
	/** BDU Admission Process — numbered step cards. */
	APPLICATION_PROCESS_1030: "1030",
	/** KUK Admission Process — numbered steps beside the inline lead form. */
	APPLICATION_PROCESS_5011: "5011",
};

export const SKILLS_VARIANTS = {
	SKILLS_1: "1",
};

export const WHO_IS_THIS_PROGRAM_FOR_VARIANTS = {
	WHO_IS_THIS_PROGRAM_FOR_1: "1",
};

export const BANNER_VIDEO_VARIANTS = {
	BANNER_VIDEO_1: "1",
};

export const COMPARISON_VARIANTS = {
	COMPARISON_1: "1",
	COMPARISON_1004: "1004",
};

export const LEADERS_VARIANTS = {
	LEADERS_1: "1",
	/** BDU Leadership — bordered portrait cards in a scroll rail. */
	LEADERS_1032: "1032",
};

export const WALK_THROUGH_VARIANTS = {
	WALK_THROUGH_1: "1",
};

export const LIFE_MILESTONE_VARIANTS = {
	LIFE_MILESTONE_1: "1",
	LIFE_MILESTONE_2: "2",
	LIFE_MILESTONE_3: "3",
};

export const ENROLLMENTS_VARIANTS = {
	ENROLLMENTS_1: "1",
	ENROLLMENTS_2: "2",
	ENROLLMENTS_3: "3",
	ENROLLMENTS_4: "4",
	ENROLLMENTS_5: "5",
};

// University Settings Section Types
export const UNIVERSITY_SETTINGS_SECTION_TYPES = {
	LEAD_DROP_POPUP: "1",
	LEAD_DROP_FORM: "2",
	COURSES: "3",
	HIGHER_QUALIFICATION: "4",
	UNIVERSITY_DETAILS: "5",
	APP_CONFIG_UNIVERSITY: "6",
	APP_CONFIG_APP: "7",
};

// Lead Drop Popup Variants (stored in university model, not page sections)
export const LEAD_DROP_POPUP_VARIANTS = {
	LEAD_DROP_POPUP_1: "1",
	LEAD_DROP_POPUP_2: "2",
	// Variant 3: "Drop a query" — saves to the CMS DB only (POST /leads/direct), no OTP/CRM.
	LEAD_DROP_POPUP_3: "3",
	// Variant 4: no-OTP submit → CRM push (POST /leads/sync-drop4); ERP always, LSQ only for multi-tenant microsites.
	LEAD_DROP_POPUP_4: "4",
	LEAD_DROP_POPUP_7: "7",
	LEAD_DROP_POPUP_10: "10",
	LEAD_DROP_POPUP_6: "6",
	LEAD_DROP_POPUP_8: "8",
	LEAD_DROP_POPUP_9: "9",
	// Variant 2001: single-step popup with the OTP revealed inline once the
	// phone validates. A course flagged `is_certification` continues to a second
	// step (background details) and LMS signup; every other course finishes on the
	// regular success card.
	LEAD_DROP_POPUP_2001: "2001",
	// Variant 5002: BDU-style single-step popup — all five fields on one screen with
	// the OTP box revealed inline once name/phone/email validate (no second step).
	LEAD_DROP_POPUP_5002: "5002",
	// Variant 5018: KUK-style phone-first popup — the phone alone, then the remaining
	// fields revealed for a new lead, then a SEPARATE OTP screen.
	LEAD_DROP_POPUP_5018: "5018",
};

export const LEAD_DROP_FORM_VARIANTS = {
	LEAD_DROP_FORM_1: "1",
	LEAD_DROP_FORM_2: "2",
	// Variant 3: "Drop a query" — saves to the CMS DB only (POST /leads/direct), no OTP/CRM.
	LEAD_DROP_FORM_3: "3",
	// Variant 4: no-OTP submit → CRM push (POST /leads/sync-drop4); ERP always, LSQ only for multi-tenant microsites.
	LEAD_DROP_FORM_4: "4",
	LEAD_DROP_FORM_10: "10",
	LEAD_DROP_FORM_6: "6",
	LEAD_DROP_FORM_8: "8",
	LEAD_DROP_FORM_9: "9",
	// Variant 2001: inline twin of popup 2001 (same card, no close button).
	LEAD_DROP_FORM_2001: "2001",
	// Variant 5002: inline twin of popup 5002 (BDU-style single-step + inline OTP).
	LEAD_DROP_FORM_5002: "5002",
	// Variant 5018: inline twin of popup 5018 (KUK phone-first + separate OTP screen).
	LEAD_DROP_FORM_5018: "5018",
};

export const COURSES_VARIANTS = {
	COURSES_1: "1",
};

export const HIGHER_QUALIFICATION_VARIANTS = {
	HIGHER_QUALIFICATION_1: "1",
};

export const UNIVERSITY_VARIANTS = {
	UNIVERSITY_1: "1",
};

export const SKILL_CARDS_VARIANTS = {
	SKILL_CARDS_1: "3000"
}
